import client from "../client/client";

const getUserIdFromSession = () => {
  const userId = sessionStorage.getItem("User");

  if (!userId) {
    throw new Error("No se encontró el usuario en sessionStorage (User)");
  }

  const parsedUserId = parseInt(userId, 10);
  if (!Number.isFinite(parsedUserId)) {
    throw new Error("El user_id en sesión no es válido");
  }

  return parsedUserId;
};

/** Base URL (sin query) a la que el API debe redirigir después del OAuth de Mercado Libre. */
const buildMercadoLibrePostAuthRedirectUrl = () => {
  const fromEnv = (process.env.REACT_APP_ML_POST_AUTH_REDIRECT_URL || "").trim().replace(/\/$/, "");
  if (fromEnv) return fromEnv;
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/integrations`;
};

/** Base URL (sin query) a la que el API debe redirigir después del OAuth de Tienda Nube. */
const buildTiendaNubePostAuthRedirectUrl = () => {
  const fromEnv = (process.env.REACT_APP_TN_POST_AUTH_REDIRECT_URL || "").trim().replace(/\/$/, "");
  if (fromEnv) return fromEnv;
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/integrations`;
};

export const INTEGRATION_CONFIG_REQUIRED_MESSAGE =
  "Es necesario configurar las integraciones para poder obtener cotizaciones.";

export const INTEGRATION_NOT_CONFIGURED_CODE = "INTEGRATION_NOT_CONFIGURED";
const NO_SHIPPING_PROVIDERS_ERROR = "no shipping providers available";

const getApiErrorText = (error) => {
  const data = error?.response?.data;

  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    return (
      data.message ||
      data.detail ||
      data.error ||
      data.msg ||
      (Array.isArray(data.errors) ? data.errors.join(" ") : "") ||
      ""
    );
  }

  return error?.message || "";
};

const normalizePostalCode = (postalCode) => {
  const trimmed = String(postalCode ?? "").trim();
  if (!trimmed) {
    throw new Error("El código postal es requerido para cotizar el envío.");
  }

  const digits = trimmed.replace(/\D/g, "");
  const parsed = parseInt(digits, 10);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error("El código postal no es válido.");
  }

  return parsed;
};

const getDeliberyQuotes = async (product_ids = [], postal_code, packages = []) => {
  const userId = getUserIdFromSession();

  const params = {
    user_id: userId,
    product_ids,
    postal_code: normalizePostalCode(postal_code),
    // Envío convencional: si el operador cargó bultos con medidas en la creación,
    // se cotiza con esos bultos (más preciso que estimar por producto).
    ...(Array.isArray(packages) && packages.length
      ? {
          packages: packages.map((p) => ({
            width: Number(p.width) || 0,
            height: Number(p.height) || 0,
            depth: Number(p.depth) || 0,
            total_weight: Number(p.total_weight) || 0,
          })),
        }
      : {}),
  };

  try {
    const resp = await client.post(`/integrations-api/delibery/quotes`, params);
    return resp.data;
  } catch (error) {
    const backendErrorMessage = getApiErrorText(error);

    if (backendErrorMessage.toLowerCase().includes(NO_SHIPPING_PROVIDERS_ERROR)) {
      const configError = new Error(INTEGRATION_CONFIG_REQUIRED_MESSAGE);
      configError.code = INTEGRATION_NOT_CONFIGURED_CODE;
      throw configError;
    }

    console.error("Error al obtener cotizaciones de envío:", error);
    throw error;
  }
};

const saveMercadoLibreCredentials = async ({ clientId, clientSecret }) => {
  const postAuthRedirectUrl = buildMercadoLibrePostAuthRedirectUrl();
  const payload = {
    user_id: getUserIdFromSession(),
    client_id: clientId,
    client_secret: clientSecret,
    ...(postAuthRedirectUrl ? { post_auth_redirect_url: postAuthRedirectUrl } : {}),
  };

  const response = await client.post("/integrations-api/mercadolibre/credentials", payload);
  return response?.data ?? {};
};

const saveTiendanubeCredentials = async ({ clientId, clientSecret }) => {
  const postAuthRedirectUrl = buildTiendaNubePostAuthRedirectUrl();
  const payload = {
    user_id: getUserIdFromSession(),
    client_id: clientId,
    client_secret: clientSecret,
    ...(postAuthRedirectUrl ? { post_auth_redirect_url: postAuthRedirectUrl } : {}),
  };

  const response = await client.post("/integrations-api/tiendanube/credentials", payload);
  return response?.data ?? {};
};

const saveWooCommerceCredentials = async ({ storeUrl, consumerKey, consumerSecret }) => {
  const payload = {
    user_id: getUserIdFromSession(),
    store_url: storeUrl,
    consumer_key: consumerKey,
    consumer_secret: consumerSecret,
  };

  const response = await client.post("/integrations-api/woocommerce/credentials", payload);
  return response?.data ?? {};
};

// Guarda las credenciales (client_id + secret) de una paquetería. Mismo modelo que
// los marketplaces, sin OAuth: la conexión queda activa apenas se guardan.
// partner: "andreani" | "urbano" | "enviopack" | "epsa".
const saveLogisticsCredentials = async (partner, { clientId, clientSecret }) => {
  const payload = {
    user_id: getUserIdFromSession(),
    client_id: clientId,
    client_secret: clientSecret,
  };

  const response = await client.post(
    `/integrations-api/logistics/${partner}/credentials`,
    payload
  );
  return response?.data ?? {};
};

const getIntegrations = async () => {
  const userId = getUserIdFromSession();
  const response = await client.get("/integrations-api/integrations", {
    params: { user_id: userId },
  });
  return response?.data?.integrations ?? [];
};

const deleteIntegration = async (id) => {
  const userId = getUserIdFromSession();
  const response = await client.delete(`/integrations-api/integrations/${id}`, {
    params: { user_id: userId },
  });
  return response?.data ?? {};
};

// Avisa a MercadoLibre que una orden fue entregada (tag "delivered"). Para pedidos
// no_shipping (Cliente retira): se llama al despachar/entregar el egreso en el WMS.
const notifyMercadoLibreDelivered = async (orderId) => {
  const payload = { user_id: getUserIdFromSession(), order_id: String(orderId) };
  const response = await client.post("/integrations-api/mercadolibre/orders/delivered", payload);
  return response?.data ?? {};
};

// Baja la etiqueta ME2/Flex de una orden ML ON-DEMAND (al imprimir). El backend pide el
// PDF a ML en ese momento → recién ahí ML marca el envío "impreso". Devuelve un Blob PDF.
const getMercadoLibreLabel = async (orderId) => {
  const userId = getUserIdFromSession();
  const resp = await client.get("/integrations-api/mercadolibre/label", {
    params: { user_id: userId, order_id: String(orderId) },
    responseType: "blob",
  });
  return resp.data;
};

// Baja la etiqueta de la paquetería (carrier) de un envío ya creado. Devuelve un Blob PDF
// que normalmente trae una etiqueta por bulto (el envío se crea con los N bultos).
const getLogisticsLabel = async (movementLogisticsId) => {
  const resp = await client.get(
    `/integrations-api/logistics/${movementLogisticsId}/label`,
    { responseType: "blob" }
  );
  return resp.data;
};

// Crea el envío en la paquetería (si todavía no existe tracking) para poder bajar la
// etiqueta. Best-effort en el flujo normal; acá se usa como fallback antes de imprimir.
const createLogisticsShipment = async (movementLogisticsId) => {
  const resp = await client.post(
    `/integrations-api/logistics/${movementLogisticsId}/shipment`
  );
  return resp.data;
};

const integrationService = {
  getDeliberyQuotes,
  getMercadoLibreLabel,
  getLogisticsLabel,
  createLogisticsShipment,
  saveMercadoLibreCredentials,
  saveTiendanubeCredentials,
  saveWooCommerceCredentials,
  saveLogisticsCredentials,
  getIntegrations,
  deleteIntegration,
  notifyMercadoLibreDelivered,
};

export default integrationService;
